import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextButton } from "@/components/common/text-button";
import { Image } from "@/components/common/image";
import { Video } from "@/components/common/video";
import { Upload } from "@/components/common/upload";
import { CropModal } from "@/components/common/crop-modal";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/utils/cn";
import { validateVideoReason, VideoInvalid } from "@/utils/video-validator-reason";
import { validateImage } from "@/utils/image-validator";
import {
  ReuploadIcon,
  UploadIcon,
  ImageRule1Image,
  ImageRule2Image,
  ImageRule3Image,
  ImageRule4Image,
  VideoRule1Image,
  VideoRule2Image,
  VideoRule3Image,
  VideoRule4Image,
} from "./assets";
import {
  ALLOW_IMAGE_TYPES,
  ALLOW_IMAGE_MAX_SIZE,
  ALLOW_IMAGE_MIN_SIZE,
  ALLOW_VIDEO_TYPES,
  ALLOW_VIDEO_MAX_SIZE,
  ALLOW_VIDEO_MAX_DURATION,
  ALLOW_VIDEO_MIN_DURATION,
  ALLOW_VIDEO_MIN_RESOLUTION,
  ALLOW_VIDEO_MAX_RESOLUTION,
} from "./constants";
import { ImageInvalid, validateImageReason } from "@/utils/image-validator-reason";
import { UPLOAD_ERROR_TYPE } from "@/hooks/use-upload";

interface UploadContentProps {
  uploadType: "image" | "video";
  remoteUrl?: string;
}

// 上传前展示的内容
const UploadContent = ({ uploadType, remoteUrl }: UploadContentProps) => {
  const { t } = useTranslation();
  const tips =
    uploadType === "image"
      ? t("custom_digital_human.upload_form.upload_image_tips")
      : t("custom_digital_human.upload_form.upload_video_tips");
  const rules =
    uploadType === "image"
      ? [ImageRule1Image, ImageRule2Image, ImageRule3Image, ImageRule4Image]
      : [VideoRule1Image, VideoRule2Image, VideoRule3Image, VideoRule4Image];

  return (
    <>
      <div className="mb-3 flex justify-center items-center border border-[rgba(213,215,218,1)] w-10 h-10 bg-white rounded-sm">
        <UploadIcon className="w-5 h-5" />
      </div>
      <p className="mb-1 space-x-1">
        <span className="text-sm text-[rgba(117,66,247,1)] font-semibold">
          {t("custom_digital_human.upload_form.click_to_upload")}
        </span>
        <span className="text-sm text-[rgba(52,64,84,1)] font-medium">
          {t("custom_digital_human.upload_form.drag_drop")}
        </span>
      </p>
      <p className="mb-4 text-xs text-[rgba(102,112,133,1)]">{tips}</p>
      <p className="mb-3 text-xs text-[rgba(102,112,133,1)]">
        {t("custom_digital_human.upload_form.upload_tips")}
      </p>
      <div className="flex justify-center items-center gap-2">
        {rules.map((Rule) => (
          <Rule className="w-20 rounded-md" />
        ))}
      </div>
    </>
  );
};

interface UploadedContentProps {
  file: File;
  remoteUrl?: string;
  uploadType: "image" | "video";
  onReCrop: () => void;
}

// 上传/裁剪后展示的内容
const UploadedContent = ({ file, remoteUrl, uploadType, onReCrop }: UploadedContentProps) => {

  return (
    <div className="absolute inset-0 flex justify-center items-center">
      {(file ? file.type.startsWith("image/") : '') || uploadType === "image" ? (
        <Image
          className="h-full rounded-xl"
          src={file ? URL.createObjectURL(file) : remoteUrl}
          onClick={(e) => {
            e.stopPropagation();
            onReCrop();
          }}
        />
      ) : (
        <Video
          className="h-full rounded-xl"
          src={file ? URL.createObjectURL(file) : remoteUrl}
          onClick={(e) => {
            e.stopPropagation();
            onReCrop();
          }}
        />
      )}
      <ReuploadIcon className="absolute bottom-2 right-2 w-8 h-8" />
    </div>
  );
};

interface CropCoords {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface UploadFormProps {
  className?: string;
  uploadType: "image" | "video";
  file?: File;
  remoteUrl?: string;
  onSubmit: (file: File) => void;
}

export const UploadForm = ({
  className,
  uploadType,
  file,
  remoteUrl,
  onSubmit,
}: UploadFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  // 上传的文件
  const [selectedFile, setSelectedFile] = useState<File | null>(file);
  // 裁剪后的文件
  const [cropedFile, setCropedFile] = useState<File | null>(null);
  // 裁剪坐标
  const [cropCoords, setCropCoords] = useState<CropCoords | null>(null);
  // 同意确认状态
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  // 裁剪弹窗是否打开
  const [cropModalOpen, setCropModalOpen] = useState(false);

  // 上传文件
  const handleUpload = async (file: File) => {
    // 校验文件是否有效
    if (uploadType === "image") {
      const validResult = await validateImageReason(file, {
        allowedTypes: ALLOW_IMAGE_TYPES,
        minFileSize: ALLOW_IMAGE_MIN_SIZE,
        maxFileSize: ALLOW_IMAGE_MAX_SIZE,
      });
      if (!validResult.isValid) {
        let errorMessage = "custom_digital_human.upload_form.invalid_image_description"
        switch (validResult.errorType) {
          case ImageInvalid.FILE_SIZE:
            errorMessage = t("custom_digital_human.upload_form.invalid_image_filesize");
            break;
        }
        toast.default({
          title: t("custom_digital_human.upload_form.invalid_image"),
          description: t(errorMessage),
        });
        return;
      }
      setSelectedFile(file);
      // 图片需要裁剪
      setCropModalOpen(true);
    } else if (uploadType === "video") {
      const validResult = await validateVideoReason(file, {
        allowedTypes: ALLOW_VIDEO_TYPES,
        maxFileSize: ALLOW_VIDEO_MAX_SIZE,
        minHeight: ALLOW_VIDEO_MIN_RESOLUTION,
        maxHeight: ALLOW_VIDEO_MAX_RESOLUTION,
        minDuration: ALLOW_VIDEO_MIN_DURATION,
        maxDuration: ALLOW_VIDEO_MAX_DURATION,
      });
      if (!validResult.isValid) {
        let errorMessage = "custom_digital_human.upload_form.invalid_video_description";
        switch (validResult.errorType) {
          case VideoInvalid.FILE_SIZE:
            errorMessage = t("custom_digital_human.upload_form.invalid_video_filesize");
            break;
          case VideoInvalid.RESOLUTION:
            errorMessage = t("custom_digital_human.upload_form.invalid_video_resolution");
            break;
          case VideoInvalid.DURATION:
            errorMessage = t("custom_digital_human.upload_form.invalid_video_duration");
            break;
        }
        toast.default({
          title: t("custom_digital_human.upload_form.invalid_video"),
          description: errorMessage,
        });
        return;
      }
      setSelectedFile(file);
      // 视频不需要裁剪
      // setCropModalOpen(true);
      // 直接输出为裁剪后的文件
      setCropedFile(file);
    }
  };

  // 上传校验失败
  const handleInvalid = (reason?: keyof typeof UPLOAD_ERROR_TYPE) => {
    if (uploadType === "image") {
      let errorMessage = "custom_digital_human.upload_form.invalid_image_description"
      if (reason === UPLOAD_ERROR_TYPE.INVALID_MAX_SIZE) {
        errorMessage = t("custom_digital_human.upload_form.invalid_image_filesize")
      }
      toast.default({
        title: t("custom_digital_human.upload_form.invalid_image"),
        description: t(errorMessage),
      });
    } else if (uploadType === "video") {
      let errorMessage = "custom_digital_human.upload_form.invalid_video_description"
      if (reason === UPLOAD_ERROR_TYPE.INVALID_MAX_SIZE) {
        errorMessage = t("custom_digital_human.upload_form.invalid_video_filesize")
      }
      toast.default({
        title: t("custom_digital_human.upload_form.invalid_video"),
        description: t(errorMessage),
      });
    }
  };

  // 重新裁剪
  const handleReCrop = () => {
    if (uploadType === "image") {
      setCropModalOpen(true);
    }
  };

  // 取消裁剪
  const handleCancelCrop = (open: boolean) => {
    if (!open) {
      setCropModalOpen(false);
    }
  };

  // 保存裁剪
  const handleSaveCrop = (file: File, coords: CropCoords) => {
    setCropedFile(file);
    setCropCoords(coords);
    setCropModalOpen(false);
  };

  // 提交表单
  const handleSubmit = async () => {
    if (cropedFile || remoteUrl) {
      onSubmit(cropedFile);
    }
  };

  // 数据同步
  useEffect(() => {
    if (file) {
      if (uploadType === "image") {
        setSelectedFile(file);
        // 恢复裁剪坐标初始状态
        setCropCoords(null);
        // 默认打开裁剪弹窗
        setCropModalOpen(true);
      } else if (uploadType === "video") {
        setSelectedFile(file);
        // 视频不需要裁剪
        // setCropCoords(null);
        // setCropModalOpen(true);
        // 直接输出为裁剪后的文件
        setCropedFile(file);
      }
    }
  }, [file]);

  const acceptedFileTypes =
    uploadType === "image"
      ? ALLOW_IMAGE_TYPES.join(",")
      : ALLOW_VIDEO_TYPES.join(",");
  const minFileSize = uploadType === "image" ? ALLOW_IMAGE_MIN_SIZE : 0;
  const maxFileSize =
    uploadType === "image" ? ALLOW_IMAGE_MAX_SIZE : ALLOW_VIDEO_MAX_SIZE;
  return (
    <>
      <div
        className={cn(
          "flex flex-col items-center justify-center px-20",
          className
        )}
      >
        <Upload
          className={cn(
            "relative flex-1 flex flex-col items-center justify-center overflow-hidden rounded-[1.25rem] mb-2 w-full bg-[rgba(249,250,251,1)] transition-colors cursor-pointer",
            "border-2 border-dashed border-[rgba(208,213,221,1)] hover:border-[rgba(102,112,133,1)]",
            Boolean(cropedFile) && "hover:border-[rgba(208,213,221,1)]"
          )}
          dragOverClassName="border-i-primary bg-blue-50"
          acceptedFileTypes={acceptedFileTypes}
          minFileSize={minFileSize}
          maxFileSize={maxFileSize}
          onUpload={handleUpload}
          onInvalid={handleInvalid}
        >
          {cropedFile || remoteUrl ? (
            <UploadedContent
              file={cropedFile}
              remoteUrl={remoteUrl}
              uploadType={uploadType}
              onReCrop={handleReCrop}
            />
          ) : (
            <UploadContent
              uploadType={uploadType}
              remoteUrl={remoteUrl}
            />
          )}
        </Upload>
        <div className="flex justify-start items-start mb-4 gap-1">
          <input
            type="checkbox"
            id="consent-checkbox"
            checked={isConsentChecked}
            onChange={(e) => setIsConsentChecked(e.target.checked)}
            className="-mt-0.5 w-5 h-5 border-gray-300 rounded cursor-pointer "
            style={{ accentColor: '#7542F7' }}
          />
          <label
            htmlFor="consent-checkbox"
            className="text-xs text-gray-700 cursor-pointer"
          >
            {t("custom_digital_human.upload_form.consent_agreement")}{" "}
            <a
              href="https://bizdesk.ai/app/user_agreement.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {t("custom_digital_human.upload_form.terms_of_service")}
            </a>{" "}
            {t("common.and")}{" "}
            <a
              href="https://bizdesk.ai/app/privacy_policy.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {t("custom_digital_human.upload_form.privacy_policy")}
            </a>
            。
          </label>
        </div>
        <TextButton
          className="w-80"
          label={t("custom_digital_human.upload_form.next")}
          disabled={(!cropedFile && !remoteUrl) || !isConsentChecked}
          onClick={() => handleSubmit()}
        />
      </div>
      <CropModal
        open={cropModalOpen && !remoteUrl}
        file={selectedFile}
        defaultCropCoords={cropCoords}
        onOpenChange={handleCancelCrop}
        onCropComplete={handleSaveCrop}
      />
    </>
  );
};
